import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES, SITE } from "@/lib/site";
import { Link } from "react-router-dom";
import { Scale, Eye, Banknote } from "lucide-react";
import { GoldRate } from "./GoldRate";

const steps = [
  {
    icon: Eye,
    title: "Watch every test",
    text: "Your gold is tested and weighed on the counter, in front of you. Nothing leaves your sight.",
  },
  {
    icon: Scale,
    title: "Honest valuation",
    text: "Transparent pricing based on the live gold rate, explained clearly before you decide anything.",
  },
  {
    icon: Banknote,
    title: "Paid on the spot",
    text: "Fair, competitive offer based on the live gold rate — walk out with cash the same visit.",
  },
];

export const WeBuyGold = () => {
  return (
    <>
      <GoldRate />
      <section id="sell-gold" data-testid="sell-gold-section" className="py-24 lg:py-32 bg-[#F5F3EC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <div className="overflow-hidden rounded-sm">
              <img
                src={IMAGES.buyGold}
                alt="Gold bracelet presented for appraisal"
                data-testid="sell-gold-image"
                className="w-full h-[440px] object-cover hover:scale-105 transition-transform duration-700 ease-out"
              />
            </div>
            <div className="absolute -bottom-8 -right-4 lg:-right-8 bg-white border border-[#E8DCC4] rounded-sm px-6 py-5 shadow-[0_20px_40px_rgb(184,134,11,0.10)]">
              <p className="font-display text-3xl text-[#C9A227]" data-testid="sell-gold-badge-title">Top payouts</p>
              <p className="text-sm text-neutral-500 mt-1">Paid on the spot, in cash</p>
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-7 order-1 lg:order-2 lg:pl-8">
          <Reveal>
            <Eyebrow>Sell Your Gold</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="sell-gold-heading">
              We buy gold — <span className="text-[#C9A227]">honestly,</span> and for more.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600 max-w-xl">
              Jewelry, coins, bullion, broken pieces — bring it in. We specialize in 10k, 14k and
              18k gold, tested and weighed in front of you, with a competitive offer paid on the spot.
            </p>
            <div className="mt-8 flex gap-4" data-testid="karat-badges">
              {["10K", "14K", "18K"].map((k) => (
                <span
                  key={k}
                  data-testid={`karat-badge-${k.toLowerCase()}`}
                  className="w-14 h-14 rounded-full bg-gradient-to-br from-[#F0D988] to-[#C9A227] text-neutral-900 font-display font-semibold flex items-center justify-center shadow-[0_8px_20px_rgb(201,162,39,0.25)]"
                >
                  {k}
                </span>
              ))}
            </div>
          </Reveal>

          <div className="mt-12 space-y-8">
            {steps.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={0.1 * (i + 1)}>
                <div className="flex items-start gap-5" data-testid={`sell-gold-step-${i}`}>
                  <div className="w-12 h-12 shrink-0 rounded-full bg-white border border-[#E8DCC4] flex items-center justify-center">
                    <Icon size={20} strokeWidth={1} className="text-[#C9A227]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-800">{title}</h3>
                    <p className="text-sm md:text-base text-neutral-600 mt-1 leading-relaxed max-w-md">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4}>
            <Link
              to="/sell"
              data-testid="sell-gold-cta"
              className="mt-12 inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white text-sm font-semibold px-8 py-3.5 hover:bg-[#C9A227] hover:text-neutral-900 hover:-translate-y-0.5 transition-[background-color,color,transform] duration-300"
            >
              Sell your gold, watches & diamonds
            </Link>
            <p className="mt-4 text-sm text-neutral-500">
              Selling a Swiss watch? Visit our sister site{" "}
              <a
                href={SITE.watchBuyersUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sell-gold-watch-buyers-link"
                className="font-semibold text-[#C9A227] hover:text-[#A8871B] transition-colors duration-300"
              >
                {SITE.watchBuyersName}
              </a>{" "}
              — top-dollar valuations for Rolex, Omega, Patek Philippe &amp; more.
            </p>
          </Reveal>
        </div>
      </div>
      </section>
    </>
  );
};
